// Safe App - Mobile-First Production Safety Management System
// Technical Council of Elders - Production Ready Application

import React, { useState, useEffect } from 'react';
import { List, ShieldAlert, FileText, CheckSquare, Key, Link2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { io } from 'socket.io-client';
import './App.css';
import './i18n';

// Components
import ThemeToggle from './components/ThemeToggle';
import LanguageSwitcher from './components/LanguageSwitcher';
import LogForm from './components/LogForm';
import LogList from './components/LogList';
import SJAWizard from './components/SJAWizard';
import DocumentList from './components/DocumentList';
import ChecklistMode from './components/ChecklistMode';
import HandoverWizard from './components/HandoverWizard';
import ArenaIngestor from './components/ArenaIngestor';
import MobileConnect from './components/MobileConnect';
import LoadingScreen from './components/LoadingScreen';
import { Button } from './components/ui/button';

// API
import { api } from './api/client';

function App() {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('new'); // 'new', 'list', 'sja', 'checks', 'handover', 'documents', 'hub'
  const [refreshLogList, setRefreshLogList] = useState(0);
  const [revelationActive, setRevelationActive] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [socket, setSocket] = useState(null);

  // Feature Flags (Default to true if not set)
  const SHOW_SJA = import.meta.env.VITE_ENABLE_SJA !== 'false';
  const SHOW_CHECKS = import.meta.env.VITE_ENABLE_CHECKS !== 'false';
  const SHOW_HANDOVER = import.meta.env.VITE_ENABLE_HANDOVER !== 'false';
  const SHOW_DOCS = import.meta.env.VITE_ENABLE_DOCS !== 'false';
  const SHOW_HUB = import.meta.env.VITE_ENABLE_HUB !== 'false';

  // Initialize application
  useEffect(() => {
    const initializeApp = async () => {
      try {
        // Initialize WebSocket connection
        const socketConnection = io('http://localhost:3002', {
          transports: ['websocket', 'polling']
        });

        socketConnection.on('connect', () => {
          console.log('🔄 Real-time connection established');
          setSocket(socketConnection);
        });

        socketConnection.on('connected', (data) => {
          console.log('📡 Server welcome:', data);
        });

        socketConnection.on('disconnect', () => {
          console.log('📡 Real-time connection lost');
        });

        // Set up online/offline detection
        const handleOnline = () => setIsOnline(true);
        const handleOffline = () => setIsOnline(false);
        
        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);

        setIsLoading(false);

        return () => {
          window.removeEventListener('online', handleOnline);
          window.removeEventListener('offline', handleOffline);
          if (socketConnection) {
            socketConnection.disconnect();
          }
        };

      } catch (error) {
        console.error('❌ App initialization failed:', error);
        setIsLoading(false);
      }
    };

    initializeApp();
  }, []);

  const handleLogSubmitted = () => {
    setRefreshLogList(prev => prev + 1);
    setActiveTab('list'); // Switch to list view after submission
  };

  const NavButton = ({ active, onClick, icon: Icon, label }) => (
    <button
      onClick={onClick}
      className={`flex flex-col items-center justify-center w-full h-full space-y-1 ${active ? 'text-blue-600' : 'text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200'} transition-all`}
    >
      <Icon className={`h-6 w-6 ${active ? 'fill-current' : ''}`} />
      <span className="text-[10px] font-medium">{label}</span>
    </button>
  );

  if (isLoading) {
    return <LoadingScreen />;
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-zinc-950 flex flex-col font-sans relative transition-colors duration-300">

      {/* THE REVELATION PORTAL LAYER */}
      <div className={`revelation-overlay pointer-events-none fixed inset-0 z-[9999] transition-opacity duration-1000 ${revelationActive ? 'opacity-100' : 'opacity-0'}`} />

      {/* Header */}
      <header className="bg-white dark:bg-zinc-900 border-b dark:border-zinc-700 sticky top-0 z-10 transition-colors duration-300">
        <div className="max-w-md mx-auto px-4 h-16 flex items-center justify-between">
          <div
            className="flex items-center gap-2 group"
            onClick={() => setActiveTab('new')}
            onMouseEnter={() => setRevelationActive(true)}
            onMouseLeave={() => setRevelationActive(false)}
          >
            <div className="h-8 w-8 rounded-lg flex items-center justify-center cursor-pointer overflow-hidden transition-transform group-hover:scale-110 duration-500">
              <img src="/venue_logo.png" alt="Logo" className="h-full w-full object-contain" onError={(e) => { e.target.style.display = 'none'; e.target.parentElement.classList.add('bg-blue-600'); e.target.parentElement.innerHTML = '<span class="text-white font-bold">S</span>' }} />
            </div>
            <h1 className="text-xl font-bold tracking-tight text-gray-900 dark:text-white cursor-pointer hidden sm:block px-2">
              {import.meta.env.VITE_APP_NAME || "Safe App"}
            </h1>
          </div>

          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1">
              <MobileConnect />
              <ThemeToggle />
              <LanguageSwitcher />
            </div>
            {!isOnline && (
              <span className="px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-800 rounded-full">
                Offline
              </span>
            )}
          </div>
        </div>

        {/* Sub-header Navigation */}
        <div className="max-w-md mx-auto px-4 py-2 border-b dark:border-zinc-700 bg-gray-50/50 dark:bg-zinc-900/50 backdrop-blur-sm overflow-x-auto flex items-center gap-2">
          {['new', 'list', 'sja', 'checks', 'handover', 'documents'].map(key => {
            if (
              (key === 'sja' && !SHOW_SJA) ||
              (key === 'checks' && !SHOW_CHECKS) ||
              (key === 'handover' && !SHOW_HANDOVER) ||
              (key === 'documents' && !SHOW_DOCS)
            ) return null;

            const labelMap = {
              new: t('log', 'Log'),
              list: t('feed', 'Feed'),
              sja: t('sja', 'SJA'),
              checks: t('checks', 'Checks'),
              handover: t('handover', 'Keys'),
              documents: t('documents', 'Docs')
            };

            return (
              <button
                key={key}
                onClick={() => setActiveTab(key)}
                className={`px-3 py-1 text-xs font-medium rounded-md transition-all whitespace-nowrap ${activeTab === key ? 'bg-white dark:bg-zinc-800 shadow-sm text-blue-600' : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'}`}
              >
                {labelMap[key]}
              </button>
            )
          })}

          <a
            href={import.meta.env.VITE_HANDBOOK_URL || "https://safeprod.no/portal/Safe_Portal_Driftshandbok_HTML/index.html"}
            target="_blank"
            rel="noopener noreferrer"
            className="px-3 py-1 text-xs font-medium rounded-md transition-all whitespace-nowrap text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 flex items-center"
          >
            {t('handbook', 'Handbook')}
          </a>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 p-4 pb-20 flex flex-col items-center max-w-md mx-auto w-full">
        {activeTab === 'new' && (
          <div className="w-full space-y-6">
            <div className="text-center space-y-2 mb-4">
              <h2 className="text-2xl font-bold tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-gray-900 to-gray-500 dark:from-white dark:to-gray-400">
                {t('welcome')}
              </h2>
              <p className="text-gray-500 dark:text-gray-400 text-sm">
                {t('welcome_message', 'Report safety incidents in seconds.')}
              </p>
            </div>
            <LogForm onSuccess={handleLogSubmitted} />
          </div>
        )}

        {activeTab === 'list' && (
          <LogList refreshTrigger={refreshLogList} />
        )}

        {SHOW_SJA && activeTab === 'sja' && (
          <SJAWizard onCancel={() => setActiveTab('list')} onSuccess={() => setActiveTab('list')} />
        )}

        {SHOW_CHECKS && activeTab === 'checks' && <ChecklistMode />}

        {SHOW_HANDOVER && activeTab === 'handover' && (
          <HandoverWizard onSuccess={() => setActiveTab('list')} />
        )}

        {SHOW_DOCS && activeTab === 'documents' && (
          <DocumentList />
        )}

        {SHOW_HUB && activeTab === 'hub' && <ArenaIngestor />}
      </main>

      {/* Mobile Bottom Nav */}
      <nav className="fixed bottom-0 left-0 right-0 bg-white dark:bg-zinc-900 border-t dark:border-zinc-700 z-20 transition-colors duration-300">
        <div className="flex justify-around items-center h-16 max-w-3xl mx-auto px-2">
          <NavButton active={activeTab === 'list'} onClick={() => setActiveTab('list')} icon={List} label={t('feed', 'Feed')} />

          {SHOW_CHECKS && (
            <NavButton active={activeTab === 'checks'} onClick={() => setActiveTab('checks')} icon={CheckSquare} label={t('checks', 'Checks')} />
          )}

          {SHOW_SJA && (
            <NavButton active={activeTab === 'sja'} onClick={() => setActiveTab('sja')} icon={ShieldAlert} label={t('sja', 'SJA')} />
          )}

          {SHOW_DOCS && (
            <NavButton active={activeTab === 'documents'} onClick={() => setActiveTab('documents')} icon={FileText} label={t('documents', 'Docs')} />
          )}

          {SHOW_HANDOVER && (
            <NavButton active={activeTab === 'handover'} onClick={() => setActiveTab('handover')} icon={Key} label={t('handover', 'Keys')} />
          )}
          {SHOW_HUB && (
            <NavButton active={activeTab === 'hub'} onClick={() => setActiveTab('hub')} icon={Link2} label={t('hub', 'Hub')} />
          )}
        </div>
      </nav>

      {/* PWA Install Prompt */}
      <PWAInstallPrompt />
    </div>
  );
}

// PWA Install Prompt Component
function PWAInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);

  useEffect(() => {
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowInstallPrompt(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    
    console.log(`PWA install outcome: ${outcome}`);
    setDeferredPrompt(null);
    setShowInstallPrompt(false);
  };

  const handleDismiss = () => {
    setShowInstallPrompt(false);
    setDeferredPrompt(null);
  };

  if (!showInstallPrompt) return null;

  return (
    <div className="fixed bottom-4 right-4 bg-white rounded-lg shadow-lg border p-4 max-w-sm">
      <div className="flex items-start space-x-3">
        <div className="flex-shrink-0">
          <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
            <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
            </svg>
          </div>
        </div>
        <div className="flex-1">
          <h3 className="text-sm font-medium text-gray-900">Install Driftslogg</h3>
          <p className="text-sm text-gray-500 mt-1">
            Install this app on your device for the best experience
          </p>
          <div className="mt-3 flex space-x-2">
            <button
              onClick={handleInstallClick}
              className="text-sm bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700"
            >
              Install
            </button>
            <button
              onClick={handleDismiss}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              Not now
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default App;